import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import moment from 'moment';

import PageTitle from '../../common/PageTitle';
import Pagination from '../../common/Pagination';
import SpinnerBlock from '../../common/SpinnerBlock';

import { fetchGetTakeoutLists } from '../../../actions/packing';
import { showModal } from '../../../actions/modal';

import {
  SHIPPINGS_COUNT_ON_PAGE,
  GATHER_TYPE_TRANSLATE,
  SHIPPING_STATUS_TRANSLATE,
} from '../../../config';

const Wrapper = styled('div')`
  position: relative;

  .fixed-table-body {
    overflow-x: auto;
  }

  table.table td {
    color: #72747b;

    .shipping-id,
    .shipping-date-takeout,
    .shipping-gather-type {
      color: #72747b;
      font-weight: 600;
    }
    .shipping-date-takeout {
      display: block;
    }
    .shipping-date-created,
    .shipping-time-takeout,
    .shipping-gather-place {
      color: #98a6ad;
      font-size: 12px;
    }
    .shipping-status-badge {
      position: relative;
      margin-left: 10px;
      font-size: 9px;
      text-transform: uppercase;
      border-radius: 10px;
      background: #ccc;
      border: 1px solid #fff;
      color: #296775;
      padding: 2px 8px 0 18px;
      min-width: 80px;
      text-align: center;
      height: 19px;
      &:before {
        position: absolute;
        top: 3.5px;
        left: 5px;
        content: '';
        width: 7px;
        height: 7px;
        background-color: #fff;
        border-radius: 50%;
      }
    }
  }
`;

const badgesStyle = {
  ARRIVED: {
    background: '#1abc9c',
    borderColor: '#fdebcd',
    color: '#fff',
  },
  SHIPPED: {
    background: 'rgba(74,129,212,.18)',
    color: '#4a81d4',
  },
  PARTIALLY_RETURNED: {
    background: 'rgba(79,198,225,.18)',
    color: '#4fc6e1',
  },
  RETURNED: {
    background: 'rgba(241,85,108,.18)',
    color: '#f1556c',
  },
};

const getShippingDateString = (date) => {
  const currentMoment = moment(date);
  let format = 'DD MMM';
  if (moment().diff(currentMoment, 'months') > 6) {
    format += ' YYYY';
  }
  format += ', HH:mm';
  return moment(date).format(format);
};

const ShippingPage = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { location } = history;

  const [page, setPage] = useState(undefined);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');

  const [shippings, setShippings] = useState([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async function () {
      if (location && location.state && location.state.shouldOpenShipping) {
        const result = await dispatch(
          fetchGetTakeoutLists({ id: location.state.shouldOpenShipping }),
        );
        if (result.success && result.takeoutLists && result.takeoutLists.length) {
          handleOpenSpipping({ ...result.takeoutLists[0] });
        }
      }
    })();
  }, []);

  useEffect(() => {
    (async function () {
      const result = await handleGetShippings();
      setShippings(result.takeoutLists);
      setTotalItems(result.recordsTotal);
    })();
  }, [page, search]);

  const handleGetShippings = async () => {
    let sendObj = Object.assign(
      {},
      {
        size: SHIPPINGS_COUNT_ON_PAGE,
        search,
      },
    );

    if (typeof page !== 'undefined') sendObj.page = page;

    setLoading(true);

    return await dispatch(fetchGetTakeoutLists(sendObj)).finally(() => {
      setLoading(false);
    });
  };

  const handlePageSelect = (pageIndex) => {
    setPage(pageIndex);
  };

  const handleSearch = (value) => {
    setPage(0);
    setSearch(value);
  };

  const handleNewShipping = () => {
    dispatch(showModal('ShippingOrders', { isNew: true }));
  };

  const handleOpenSpipping = (shipping) => {
    dispatch(showModal('ShippingOrders', { currentShipping: shipping }));
  };

  return (
    <Fragment>
      <PageTitle title="Реестры отгрузки" />

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row mb-2">
                <div className="col-lg-6">
                  <div className="form-inline">
                    <div className="form-group mr-2 mb-2">
                      <input
                        className="form-control search-input"
                        type="text"
                        placeholder="Поиск"
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="text-lg-right">
                    <button
                      type="button"
                      className="btn btn-danger waves-effect waves-light mb-2 mr-2"
                      onClick={handleNewShipping}
                    >
                      + Новая отгрузка
                    </button>
                  </div>
                </div>
              </div>

              <Wrapper className="bootstrap-table">
                <SpinnerBlock show={loading} />
                <div className="fixed-table-container">
                  <div className="fixed-table-body">
                    <table className="table table-centered">
                      <thead className="thead-light">
                        <tr>
                          <th>Номер</th>
                          <th>Дата отгрузки</th>
                          <th>Склад</th>
                          <th>Способ отгрузки</th>
                          <th>Статус</th>
                          <th>Заказов</th>
                        </tr>
                      </thead>

                      <tbody>
                        {shippings.map((s) => (
                          <tr key={s.id}>
                            <td onClick={() => handleOpenSpipping(s)} style={{ cursor: 'pointer' }}>
                              <span className="shipping-id">{s.id}</span>
                              <br />
                              <span className="shipping-date-created">
                                {getShippingDateString(s.date)}
                              </span>
                            </td>
                            <td>
                              <span className="shipping-date-takeout">
                                {moment(s.takeoutDate, 'YYYY-MM-DD').format('DD.MM.YYYY')}
                              </span>
                              {s.takeoutHourStart && s.takeoutHourEnd ? (
                                <span className="shipping-time-takeout">{`${s.takeoutHourStart} – ${s.takeoutHourEnd}`}</span>
                              ) : null}
                            </td>
                            <td>{s.warehouseName}</td>
                            <td>
                              {s.gatherType ? (
                                <Fragment>
                                  <span className="shipping-gather-type">
                                    {GATHER_TYPE_TRANSLATE[s.gatherType]}
                                  </span>
                                  <br />
                                  <span className="shipping-gather-place">СЦ Котляковская</span>
                                </Fragment>
                              ) : null}
                            </td>
                            <td>
                              <span className="badge" style={badgesStyle[s.status]}>
                                {SHIPPING_STATUS_TRANSLATE[s.status]}
                              </span>
                            </td>
                            <td>{s.orders && s.orders.length ? `${s.orders.length} шт` : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Wrapper>

              <Pagination total={totalItems} currentPage={page} onPageSelect={handlePageSelect} />
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ShippingPage;
